import { createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "px-6 md:px-8 pt-6 pb-20" }
const _hoisted_2 = { class: "max-w-[1040px]" }
const _hoisted_3 = {
  key: 0,
  class: "mb-6 text-sm text-red-600"
}
const _hoisted_4 = {
  key: 0,
  class: "grid gap-5 sm:grid-cols-2"
}
const _hoisted_5 = {
  key: 1,
  class: "text-sm text-gray-700"
}
const _hoisted_6 = {
  key: 2,
  class: "text-sm text-gray-700"
}
const _hoisted_7 = { class: "font-medium" }
const _hoisted_8 = {
  key: 3,
  class: "flex items-center justify-between gap-4 text-[13px] text-gray-600"
}
const _hoisted_9 = {
  key: 4,
  class: "text-[13px] leading-relaxed text-gray-500"
}
const _hoisted_10 = { class: "grid gap-5 sm:grid-cols-2" }
const _hoisted_11 = {
  key: 0,
  class: "grid gap-5 sm:grid-cols-3 border-t border-gray-100 pt-5"
}
const _hoisted_12 = {
  key: 1,
  class: "grid gap-5 sm:grid-cols-2 border-t border-gray-100 pt-5"
}
const _hoisted_13 = { class: "grid gap-5 border-t border-gray-100 pt-5" }
const _hoisted_14 = { class: "grid gap-5 sm:grid-cols-2" }

import { computed, onMounted, reactive, ref } from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'

import InputArea from '@/components/neue/InputArea.vue'
import InputField from '@/components/neue/InputField.vue'
import NeueButton from '@/components/neue/NeueButton.vue'
import NeueLoading from '@/components/neue/NeueLoading.vue'
import SelectInput from '@/components/neue/SelectInput.vue'
import SettingsSection from '@/components/neue/SettingsSection.vue'
import SaveBar from '@/components/SaveBar.vue'
import SecondaryNav from '@/components/SecondaryNav.vue'
import SecondaryTop from '@/components/SecondaryTop.vue'
import { useAgentGoLive } from '@/composables/useAgentGoLive'
import {
  AI_AGENT_GENERAL_SETTINGS,
  AI_AGENTS,
  isAgentSettingsPath,
  useAgentSections,
  useAiAgentSections
} from '@/composables/useAgentSections'
import { useAgentVoiceSettings } from '@/composables/useAgentVoiceSettings'
import { useUnsavedChanges } from '@/composables/useUnsavedChanges'
import { agentTitleForType, agentTypeForSlug, isMerchantWideType } from '@/config/agentCards'
import { blankAgentForm, buildAgentPayload } from '@/helpers/agentPayload'
import api from '@/store/api'
import { mapRelationship, mapRelationships, mapType, mapTypeIncludingInactive } from '@/store/mappers'

import AgentImpactModal from './AgentImpactModal.vue'
import AgentOfferFields from './AgentOfferFields.vue'
import AgentToggleRow from './AgentToggleRow.vue'
import {
  AGENT_DAILY_SEND_CAP,
  agentLabel,
  CATERING_AGENT_TYPE,
  cateringAgentTitle,
  isCateringAgent,
  PROACTIVE_AGENT_TYPES,
  resolveInitialAgentType
} from './agentTypes'
import AgentVoiceFields from './AgentVoiceFields.vue'


export default {
  __name: 'AgentForm',
  setup(__props) {

const route = useRoute()
const router = useRouter()
const loading = ref(true)

const findAgent = id => mapTypeIncludingInactive('agent').find(a => a.id === id)

const isNew = computed(() => !route.params.id)
const isAgentSettings = computed(() => isAgentSettingsPath(route.path))
const agent = computed(() => findAgent(route.params.id))
const agentPlaybook = computed(() => mapRelationship(agent.value?.relationships?.agent_playbook?.data))
const headerText = computed(() => {
  if (isAgentSettings.value) return cateringAgentTitle(agentPlaybook.value?.attributes?.store_name)
  if (isNew.value) return `New ${agentTitleForType(form.type) || 'agent'}`
  return agent.value ? agentLabel(agent.value, agentPlaybook.value) : ''
})

const form = reactive(blankAgentForm())

const isAbandonCart = computed(() => form.type === 'AbandonCartAgent')
const isWinback = computed(() => form.type === 'WinbackAgent')
const isReview = computed(() => form.type === 'ReviewAgent')
const winbackSegments = [
  {
    key: 'slipping',
    label: 'Slipping away',
    description: 'Customers whose ordering cadence is falling off.',
    messageHelp:
      'This is the initial message a customer receives when they fall into this category. Once a customer replies it will handle questions etc and the agent will escalate to you if needed.'
  },
  {
    key: 'lost',
    label: 'Fully lost',
    description: `This agent will winback customers who have gone quiet for a while. We will contact a maximum of ${AGENT_DAILY_SEND_CAP} per day by default and will queue customers above that limit. You can change that limit in Limits.`,
    messageHelp:
      'This is the initial message sent to customer who have gone dark. Once a customer replies it will handle questions etc and the agent will escalate to you if needed.'
  }
]

// Offers come from the merchant's discount codes, loaded only for the agents
// that can make one.
const hasOffer = computed(() => isWinback.value || isAbandonCart.value)
const discountCodes = computed(() => mapType('discount_code'))
const loadDiscountCodes = () => (hasOffer.value ? api.call('GET', 'discount_codes?included=none') : Promise.resolve())

// Only the keys the form knows, so legacy raw_context keys (free-text offers)
// are neither shown nor sent back.
const segmentFromContext = (defaults, context = {}) =>
  Object.fromEntries(
    Object.keys(defaults).map(key => [
      key,
      key === 'discount_code_id' ? idOrBlank(context[key]) : context[key] ?? defaults[key]
    ])
  )
const idOrBlank = id => (id === null || id === undefined || id === '' ? '' : String(id))
const agentSections = useAgentSections()
const aiAgentSections = useAiAgentSections()

const headerTabs = computed(() => {
  if (isNew.value) return null
  return isAgentSettings.value ? agentSections.value : aiAgentSections.value
})

const showTriggerAndOutreach = computed(() => form.type === 'UpsellAgent' || form.type === 'AbandonCartAgent')
const triggerDescription = computed(() =>
  isAbandonCart.value
    ? 'How long after a cart is abandoned before the agent reaches out?'
    : 'When should the upsell message be sent?'
)
const triggerFieldLabel = computed(() => (isAbandonCart.value ? 'Delay after abandonment' : 'Time before cutoff'))
const initialMessageDescription = computed(() =>
  isAbandonCart.value
    ? 'The initial message sent to customers. Be sure to offer assistance and see if there was a problem. Once a customer replies it will handle questions etc and the agent will escalate to you if needed.'
    : 'The initial message sent to customers. Be sure to include an offer or special you’d like to promote. Once a customer replies it will handle questions etc and the agent will escalate to you if needed.'
)

const outreachContent = ref('')
const outreachStepId = ref(null)
const playbookId = ref(null)
const saveError = ref('')

// test_mode is read-only here: saving never sends it, Go live is the only way out.
// Two modals on purpose: the save one above stays bound to `impact`, this one to the go-live flow.
const inTesting = ref(false)
const goLive = useAgentGoLive()
const confirmGoLive = async () => {
  if (await goLive.confirm()) await reload(loads(route.params.id))
}

// Catering merchants have no AI Agents area, so their one agent's page also
// carries the merchant-level voice that /ai/agents/settings holds elsewhere.
const { form: voice, load: loadVoice, save: saveVoice } = useAgentVoiceSettings()

const { hasUnsavedChanges, reload } = useUnsavedChanges([form, outreachContent, voice])

const loads = id => {
  const agentThenCodes = loadAgent(id).then(loadDiscountCodes)
  return isAgentSettings.value ? [agentThenCodes, loadVoice()] : [agentThenCodes]
}

const discard = () => reload(loads(route.params.id))

const timeTypeOptions = [
  { name: 'Minutes', value: 'minutes' },
  { name: 'Hours', value: 'hours' },
  { name: 'Days', value: 'days' }
]

const storeOptions = computed(() =>
  mapType('store').map(s => ({
    name: s.attributes.store_name || 'Store #' + s.id,
    value: s.id
  }))
)

// A store-bound agent gets its store from the dashboard card (?store_id=) when
// new and from its playbook when saved, so the picker only remains for catering
// settings and for a deep link that named no store. Merchant-wide agents
// (winback, reviews) have no store at all.
const isMerchantWide = computed(() => isMerchantWideType(form.type))
const storeKnown = ref(false)
const showStorePicker = computed(() => isAgentSettings.value || (!isMerchantWide.value && !storeKnown.value))
const storeName = computed(() => storeOptions.value.find(option => option.value === form.belongs_to_id)?.name || '')
const generalDescription = computed(() =>
  showStorePicker.value ? 'Pick the store and whether the agent is running.' : 'Whether the agent is running.'
)
const storeIdFromRoute = () => (route.query.store_id ? String(route.query.store_id) : '')

const loadAgent = id => {
  if (!id) {
    loading.value = false
    return Promise.resolve()
  }
  return api.call('GET', `agents/${id}?included=agent_playbook,agent_playbook.agent_playbook_steps`).then(() => {
    const a = findAgent(id)
    if (!a) {
      loading.value = false
      return
    }
    if (!isAgentSettings.value && isCateringAgent(a)) {
      router.replace(AI_AGENTS)
      return
    }

    form.type = a.attributes.type
    form.is_active = a.attributes.is_active
    inTesting.value = Boolean(a.attributes.test_mode)

    const playbook = mapRelationship(a.relationships?.agent_playbook?.data)
    if (playbook) {
      playbookId.value = playbook.id
      form.belongs_to_id = String(playbook.attributes.belongs_to_id || '')
      storeKnown.value = Boolean(form.belongs_to_id)
      form.trigger_time_value = playbook.attributes.trigger_time_value || ''
      form.trigger_time_type = playbook.attributes.trigger_time_type || 'minutes'

      const rawContext = playbook.attributes.raw_context || {}
      form.offer_enabled = !!rawContext.offer_enabled
      form.discount_code_id = idOrBlank(rawContext.discount_code_id)

      if (a.attributes.type === 'ReviewAgent') {
        const rc = playbook.attributes.raw_context || {}
        form.review.order_threshold = rc.order_threshold ?? 3
        form.review.google_review_link = rc.google_review_link || ''
        form.review.checkin_template = rc.checkin_template || ''
      }

      if (a.attributes.type === 'WinbackAgent') {
        const rc = playbook.attributes.raw_context || {}
        form.winback.send_cap = rc.send_cap ?? AGENT_DAILY_SEND_CAP
        form.winback.reactivation_window_days = rc.reactivation_window_days ?? 30
        form.winback.slipping = segmentFromContext(blankAgentForm().winback.slipping, rc.slipping)
        form.winback.lost = segmentFromContext(blankAgentForm().winback.lost, rc.lost)
      }

      const steps = mapRelationships(playbook.relationships?.agent_playbook_steps?.data || [])
      const outreach = steps.find(s => s.attributes.step_style === 'outreach')
      if (outreach) {
        outreachContent.value = outreach.attributes.content || ''
        outreachStepId.value = outreach.id
      }
    }

    loading.value = false
  })
}

const load = (id, typeSlug) => {
  loading.value = true
  saveError.value = ''
  Object.assign(form, blankAgentForm())
  if (!id) {
    form.type = isAgentSettings.value ? CATERING_AGENT_TYPE : resolveInitialAgentType(agentTypeForSlug(typeSlug))
    form.belongs_to_id = storeIdFromRoute()
  }
  storeKnown.value = Boolean(form.belongs_to_id)
  outreachContent.value = ''
  outreachStepId.value = null
  playbookId.value = null
  return reload(loads(id))
}

const buildPayload = () =>
  buildAgentPayload(form, {
    outreachContent: outreachContent.value,
    outreachStepId: outreachStepId.value,
    playbookId: playbookId.value
  })

// An active proactive agent starts messaging customers as soon as it is saved,
// so the merchant sees how many match (and the daily cap) and confirms first.
// An agent in testing only texts the test number, so it saves without the warning.
const impact = reactive({ open: false, estimate: null, saving: false })
const needsImpactWarning = computed(
  () => form.is_active && !inTesting.value && PROACTIVE_AGENT_TYPES.includes(form.type)
)

const save = () => {
  saveError.value = ''

  if (form.type === 'ReviewAgent' && (!form.review.checkin_template.trim() || !form.review.google_review_link.trim())) {
    saveError.value = 'Add a check-in message and a Google review link before saving.'
    return
  }

  if (!needsImpactWarning.value) return persist()

  fetchImpactEstimate().then(estimate => {
    impact.estimate = estimate
    impact.open = true
  })
}

// Dry run of the save payload against the API. Resolves to null when the
// estimate is unavailable so the warning still shows, just without a number.
const fetchImpactEstimate = () => {
  const endpoint = isNew.value ? 'agents/impact' : `agents/${route.params.id}/impact`
  return api
    .call('POST', endpoint, buildPayload(), { receive: false, save: false })
    .then(response => response.data?.data?.attributes || null)
    .catch(() => null)
}

const confirmImpact = () => {
  impact.saving = true
  persist().finally(() => {
    impact.saving = false
    impact.open = false
  })
}

const persist = () => {
  const payload = buildPayload()
  const request = isNew.value
    ? api.call('POST', 'agents', payload)
    : api.call('PUT', `agents/${route.params.id}`, payload)

  return request
    .then(() => (isAgentSettings.value ? saveVoice() : null))
    .then(() => {
      if (!isNew.value) return reload(loads(route.params.id))
      return router.push(isAgentSettings.value ? '/agent-settings' : AI_AGENTS)
    })
    .catch(error => {
      const message = error.response?.data?.errors?.[0]
      saveError.value = message || 'Unable to save right now. Please try again.'
    })
}

onMounted(() => {
  api.call('GET', 'stores?included=none')
  load(route.params.id, route.params.type)
})

onBeforeRouteUpdate((to, from) => {
  const changed =
    to.params.id !== from.params.id ||
    to.params.type !== from.params.type ||
    to.query?.store_id !== from.query?.store_id
  if (changed) load(to.params.id, to.params.type)
})

return (_ctx, _cache) => {
  const _component_router_link = _resolveComponent("router-link")

  return (_openBlock(), _createBlock(SecondaryNav, null, {
    top: _withCtx(() => [
      _createVNode(SecondaryTop, {
        text: headerText.value,
        sections: headerTabs.value
      }, {
        right: _withCtx(() => [
          (isNew.value)
            ? (_openBlock(), _createBlock(NeueButton, {
                key: 0,
                type: "primary",
                onClick: save
              }, {
                default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                  _createTextVNode("Save", -1)
                ]))]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["text", "sections"]),
      (!isNew.value)
        ? (_openBlock(), _createBlock(SaveBar, {
            key: 0,
            "has-unsaved-changes": _unref(hasUnsavedChanges),
            onOnDiscard: discard,
            onOnSave: save
          }, null, 8, ["has-unsaved-changes"]))
        : _createCommentVNode("", true)
    ]),
    main: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(NeueLoading, { loading: loading.value }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              (saveError.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(saveError.value), 1))
                : _createCommentVNode("", true),
              _createVNode(SettingsSection, {
                title: "General",
                description: generalDescription.value
              }, {
                default: _withCtx(() => [
                  (showStorePicker.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                        _createVNode(SelectInput, {
                          modelValue: form.belongs_to_id,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => ((form.belongs_to_id) = $event)),
                          name: "Store",
                          options: storeOptions.value
                        }, null, 8, ["modelValue", "options"])
                      ]))
                    : (isMerchantWide.value)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_5, " This agent works across all of your stores. "))
                      : (_openBlock(), _createElementBlock("p", _hoisted_6, [
                          _cache[26] || (_cache[26] = _createTextVNode(" Store: ", -1)),
                          _createElementVNode("span", _hoisted_7, _toDisplayString(storeName.value), 1)
                        ])),
                  _createVNode(AgentToggleRow, {
                    modelValue: form.is_active,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => ((form.is_active) = $event)),
                    label: "Set live",
                    help: "Turn this agent on or off.",
                    class: "border-t border-gray-100 pt-5"
                  }, null, 8, ["modelValue"]),
                  (inTesting.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _cache[28] || (_cache[28] = _createElementVNode("p", null, "In testing: this agent only talks to your test number.", -1)),
                        _createVNode(NeueButton, {
                          type: "secondary",
                          "data-go-live": "",
                          onClick: _cache[2] || (_cache[2] = $event => (_unref(goLive).request(_unref(route).params.id, form.type)))
                        }, {
                          default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                            _createTextVNode(" Go live ", -1)
                          ]))]),
                          _: 1
                        })
                      ]))
                    : _createCommentVNode("", true),
                  (!isAgentSettings.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_9, [
                        _cache[30] || (_cache[30] = _createTextVNode(" Brand voice, tone, emoji and additional information apply to every agent. Edit them under ", -1)),
                        _createVNode(_component_router_link, {
                          to: _unref(AI_AGENT_GENERAL_SETTINGS),
                          class: "font-medium text-brand-600 hover:underline"
                        }, {
                          default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                            _createTextVNode(" General settings ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["to"])
                      ]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["description"]),
              (isAgentSettings.value)
                ? (_openBlock(), _createBlock(AgentVoiceFields, {
                    key: 1,
                    "brand-voice": _unref(voice).brand_voice_summary,
                    "onUpdate:brandVoice": _cache[3] || (_cache[3] = $event => ((_unref(voice).brand_voice_summary) = $event)),
                    emoji: _unref(voice).emoji_enabled,
                    "onUpdate:emoji": _cache[4] || (_cache[4] = $event => ((_unref(voice).emoji_enabled) = $event)),
                    "additional-context": _unref(voice).additional_context,
                    "onUpdate:additionalContext": _cache[5] || (_cache[5] = $event => ((_unref(voice).additional_context) = $event)),
                    tone: _unref(voice).tone,
                    "onUpdate:tone": _cache[6] || (_cache[6] = $event => ((_unref(voice).tone) = $event)),
                    formality: _unref(voice).formality_level,
                    "onUpdate:formality": _cache[7] || (_cache[7] = $event => ((_unref(voice).formality_level) = $event)),
                    enthusiasm: _unref(voice).enthusiasm_level,
                    "onUpdate:enthusiasm": _cache[8] || (_cache[8] = $event => ((_unref(voice).enthusiasm_level) = $event))
                  }, null, 8, ["brand-voice", "emoji", "additional-context", "tone", "formality", "enthusiasm"]))
                : _createCommentVNode("", true),
              (showTriggerAndOutreach.value)
                ? (_openBlock(), _createBlock(SettingsSection, {
                    key: 2,
                    title: "Trigger",
                    description: triggerDescription.value
                  }, {
                    default: _withCtx(() => [
                      _createElementVNode("div", _hoisted_10, [
                        _createVNode(InputField, {
                          modelValue: form.trigger_time_value,
                          "onUpdate:modelValue": _cache[9] || (_cache[9] = $event => ((form.trigger_time_value) = $event)),
                          name: triggerFieldLabel.value,
                          restrict: "number"
                        }, null, 8, ["modelValue", "name"]),
                        _createVNode(SelectInput, {
                          modelValue: form.trigger_time_type,
                          "onUpdate:modelValue": _cache[10] || (_cache[10] = $event => ((form.trigger_time_type) = $event)),
                          name: "Time type",
                          options: timeTypeOptions
                        }, null, 8, ["modelValue"])
                      ])
                    ]),
                    _: 1
                  }, 8, ["description"]))
                : _createCommentVNode("", true),
              (showTriggerAndOutreach.value)
                ? (_openBlock(), _createBlock(SettingsSection, {
                    key: 3,
                    title: "Initial message",
                    description: initialMessageDescription.value
                  }, {
                    default: _withCtx(() => [
                      _createVNode(InputArea, {
                        modelValue: outreachContent.value,
                        "onUpdate:modelValue": _cache[11] || (_cache[11] = $event => ((outreachContent).value = $event)),
                        name: "Message",
                        rows: "3"
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  }, 8, ["description"]))
                : _createCommentVNode("", true),
              (isAbandonCart.value)
                ? (_openBlock(), _createBlock(SettingsSection, {
                    key: 4,
                    title: "Offer",
                    description: "An optional incentive the agent can mention to help close the order."
                  }, {
                    default: _withCtx(() => [
                      _createVNode(AgentOfferFields, {
                        enabled: form.offer_enabled,
                        "onUpdate:enabled": _cache[12] || (_cache[12] = $event => ((form.offer_enabled) = $event)),
                        "discount-code-id": form.discount_code_id,
                        "onUpdate:discountCodeId": _cache[13] || (_cache[13] = $event => ((form.discount_code_id) = $event)),
                        codes: discountCodes.value
                      }, null, 8, ["enabled", "discount-code-id", "codes"])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (isReview.value)
                ? (_openBlock(), _createBlock(SettingsSection, {
                    key: 5,
                    title: "Reviews",
                    description: "When to ask, where to send happy customers, and the check-in message."
                  }, {
                    default: _withCtx(() => [
                      _createVNode(InputField, {
                        modelValue: form.review.order_threshold,
                        "onUpdate:modelValue": _cache[14] || (_cache[14] = $event => ((form.review.order_threshold) = $event)),
                        name: "Order threshold",
                        restrict: "integer",
                        class: "sm:max-w-xs"
                      }, null, 8, ["modelValue"]),
                      _createVNode(InputField, {
                        modelValue: form.review.google_review_link,
                        "onUpdate:modelValue": _cache[15] || (_cache[15] = $event => ((form.review.google_review_link) = $event)),
                        name: "Google review link"
                      }, null, 8, ["modelValue"]),
                      _createVNode(InputArea, {
                        modelValue: form.review.checkin_template,
                        "onUpdate:modelValue": _cache[16] || (_cache[16] = $event => ((form.review.checkin_template) = $event)),
                        name: "Check-in message",
                        rows: "3"
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  }))
                : _createCommentVNode("", true),
              (isWinback.value)
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 6 }, [
                    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(winbackSegments, (seg) => {
                      return _createVNode(SettingsSection, {
                        key: seg.key,
                        title: seg.label,
                        description: seg.description
                      }, {
                        default: _withCtx(() => [
                          _createVNode(AgentToggleRow, {
                            modelValue: form.winback[seg.key].enabled,
                            "onUpdate:modelValue": $event => ((form.winback[seg.key].enabled) = $event),
                            label: "Enabled",
                            help: "Include this segment when the agent runs."
                          }, null, 8, ["modelValue", "onUpdate:modelValue"]),
                          (form.winback[seg.key].enabled)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                (seg.key === 'slipping')
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                      _createVNode(InputField, {
                                        modelValue: form.winback.slipping.gap_multiplier,
                                        "onUpdate:modelValue": _cache[17] || (_cache[17] = $event => ((form.winback.slipping.gap_multiplier) = $event)),
                                        name: "Overdue multiplier",
                                        restrict: "number",
                                        description: "A customer is slipping when the time since their last order is this many times their usual gap between orders. 1.5 means 50% longer than usual.",
                                        "description-above": ""
                                      }, null, 8, ["modelValue"]),
                                      _createVNode(InputField, {
                                        modelValue: form.winback.slipping.min_orders,
                                        "onUpdate:modelValue": _cache[18] || (_cache[18] = $event => ((form.winback.slipping.min_orders) = $event)),
                                        name: "Minimum orders",
                                        restrict: "number",
                                        description: "Customers need at least this many paid orders before we can tell what their usual gap is.",
                                        "description-above": ""
                                      }, null, 8, ["modelValue"]),
                                      _createVNode(InputField, {
                                        modelValue: form.winback.slipping.frequency_cooldown_days,
                                        "onUpdate:modelValue": _cache[19] || (_cache[19] = $event => ((form.winback.slipping.frequency_cooldown_days) = $event)),
                                        name: "Cooldown (days)",
                                        restrict: "number",
                                        description: "Wait this many days before messaging the same customer again.",
                                        "description-above": ""
                                      }, null, 8, ["modelValue"])
                                    ]))
                                  : _createCommentVNode("", true),
                                (seg.key === 'lost')
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                      _createVNode(InputField, {
                                        modelValue: form.winback.lost.lost_window_days,
                                        "onUpdate:modelValue": _cache[20] || (_cache[20] = $event => ((form.winback.lost.lost_window_days) = $event)),
                                        name: "Lost window (days)",
                                        restrict: "number",
                                        description: "No paid order for this many days means the customer is fully lost.",
                                        "description-above": ""
                                      }, null, 8, ["modelValue"]),
                                      _createVNode(InputField, {
                                        modelValue: form.winback.lost.retouch_cooldown_days,
                                        "onUpdate:modelValue": _cache[21] || (_cache[21] = $event => ((form.winback.lost.retouch_cooldown_days) = $event)),
                                        name: "Cooldown (days)",
                                        restrict: "number",
                                        description: "Wait this many days before messaging the same customer again.",
                                        "description-above": ""
                                      }, null, 8, ["modelValue"])
                                    ]))
                                  : _createCommentVNode("", true),
                                _createVNode(InputArea, {
                                  modelValue: form.winback[seg.key].outreach_template,
                                  "onUpdate:modelValue": $event => ((form.winback[seg.key].outreach_template) = $event),
                                  name: "Initial message",
                                  rows: "3",
                                  description: seg.messageHelp,
                                  "description-above": "",
                                  class: "border-t border-gray-100 pt-5"
                                }, null, 8, ["modelValue", "onUpdate:modelValue", "description"]),
                                _createElementVNode("div", _hoisted_13, [
                                  _createVNode(AgentOfferFields, {
                                    enabled: form.winback[seg.key].offer_enabled,
                                    "onUpdate:enabled": $event => ((form.winback[seg.key].offer_enabled) = $event),
                                    "discount-code-id": form.winback[seg.key].discount_code_id,
                                    "onUpdate:discountCodeId": $event => ((form.winback[seg.key].discount_code_id) = $event),
                                    codes: discountCodes.value
                                  }, null, 8, ["enabled", "onUpdate:enabled", "discount-code-id", "onUpdate:discountCodeId", "codes"])
                                ])
                              ], 64))
                            : _createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1032, ["title", "description"])
                    }), 64)),
                    _createVNode(SettingsSection, {
                      title: "Limits",
                      description: "How many customers the agent contacts, and how a win is counted."
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_14, [
                          _createVNode(InputField, {
                            modelValue: form.winback.send_cap,
                            "onUpdate:modelValue": _cache[22] || (_cache[22] = $event => ((form.winback.send_cap) = $event)),
                            name: "Send cap",
                            restrict: "number",
                            description: `Messages per run, at most ${_unref(AGENT_DAILY_SEND_CAP)} a day. Customers above the limit wait for the next day.`,
                            "description-above": ""
                          }, null, 8, ["modelValue", "description"]),
                          _createVNode(InputField, {
                            modelValue: form.winback.reactivation_window_days,
                            "onUpdate:modelValue": _cache[23] || (_cache[23] = $event => ((form.winback.reactivation_window_days) = $event)),
                            name: "Count a win within (days)",
                            restrict: "number",
                            description: "After we message a customer, a paid order within this many days counts as won back. This is for results only and does not change who gets messaged.",
                            "description-above": ""
                          }, null, 8, ["modelValue"])
                        ])
                      ]),
                      _: 1
                    })
                  ], 64))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        }, 8, ["loading"])
      ]),
      _createVNode(AgentImpactModal, {
        "is-open": impact.open,
        estimate: impact.estimate,
        "agent-type": form.type,
        saving: impact.saving,
        onConfirm: confirmImpact,
        onCancel: _cache[24] || (_cache[24] = $event => (impact.open = false))
      }, null, 8, ["is-open", "estimate", "agent-type", "saving"]),
      _createVNode(AgentImpactModal, {
        "is-open": _unref(goLive).impactOpen.value,
        estimate: _unref(goLive).estimate.value,
        "agent-type": _unref(goLive).agentType.value,
        saving: _unref(goLive).saving.value,
        onConfirm: confirmGoLive,
        onCancel: _unref(goLive).cancel
      }, null, 8, ["is-open", "estimate", "agent-type", "saving", "onCancel"])
    ]),
    _: 1
  }))
}
}

}